// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useMemo } from 'react';
import { Api } from '../../services/config/Api';
import WorkspaceService from '../../services/workspace/WorkspaceService';
import { useDatasets } from '../../state/hooks/DatasetHooks';
import { useOrganizationId } from '../../state/hooks/OrganizationHooks';
import { useCurrentScenarioData } from '../../state/hooks/ScenarioHooks';
import { useSolutionData } from '../../state/hooks/SolutionHooks';
import { useWorkspaceId } from '../../state/hooks/WorkspaceHooks';
import { useAddTechVar, useGetDataVectorListByScenario, useGetTechVar } from '../../state/wizard/WizardHooks';
import { DatasetsUtils } from '../../utils';

export const useVecteurDonnees = () => {
  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const solutionData = useSolutionData();
  const datasets = useDatasets();
  const scenarioData = useCurrentScenarioData();
  const scenarioId = scenarioData?.id;
  const scenarioMainDataset = scenarioData?.datasetList?.[0];
  const currentScenarioParametersValues = scenarioData.parametersValues;
  const addTechVar = useAddTechVar();
  const assetClass = useGetTechVar('assetClass', scenarioId);
  const equipments = useGetDataVectorListByScenario(scenarioId);
  const equipmentsList = useMemo(() => {
    return equipments?.map((equipment) => equipment.NiveauHierarchique);
  }, [equipments]);
  const dataVectorParameter = useMemo(() => {
    return solutionData.parameters?.find((parameter) => parameter.options?.subType === 'DATAVECTOR');
  }, [solutionData.parameters]);

  const downloadFileFromStorage = useCallback(
    (fileName) => {
      const unparsedDataVectorParameter = currentScenarioParametersValues?.find(
        (parameter) => parameter.parameterId === dataVectorParameter?.id
      );
      const parsedDataVectorParameters = unparsedDataVectorParameter?.value
        ? JSON.parse(unparsedDataVectorParameter?.value)
        : [];
      const storedDatasetId = parsedDataVectorParameters?.find(
        (parameter) => parameter.parameterId === fileName
      )?.value;
      if (storedDatasetId) {
        const dataset = datasets?.find((dataset) => dataset.id === storedDatasetId);
        const storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(dataset);
        if (storageFilePath == null) return null;
        return WorkspaceService.downloadWorkspaceFile(organizationId, workspaceId, storageFilePath);
      } else {
        const storageFilePathForInitialValue = `dataset/${scenarioMainDataset}/${fileName}.csv`;
        return WorkspaceService.downloadWorkspaceFile(organizationId, workspaceId, storageFilePathForInitialValue);
      }
    },
    [
      dataVectorParameter?.id,
      currentScenarioParametersValues,
      datasets,
      organizationId,
      scenarioMainDataset,
      workspaceId,
    ]
  );

  const getAvailableColumnsList = useCallback(
    async (assetClass) => {
      const availableColumnsQuery = `MATCH(n: \`${assetClass}\`) WITH collect(n) as nodes WITH [x IN nodes 
      WHERE x.Metadata = 'AvailableColumns' | x.Values][0] AS columns RETURN columns`;
      const { data } = await Api.Datasets.twingraphQuery(organizationId, scenarioMainDataset, {
        query: availableColumnsQuery,
      });
      if (data[0].columns) return [...data[0].columns];
      return [];
    },
    [organizationId, scenarioMainDataset]
  );
  return {
    addTechVar,
    assetClass,
    scenarioId,
    equipmentsList,
    downloadFileFromStorage,
    getAvailableColumnsList,
    dataVectorParameter,
    currentScenarioParametersValues,
  };
};
